'use client';

import { useEffect, useRef, useState, useMemo } from 'react';
import { ARTICLE_AD_SLOT_NAMES } from '@/lib/constants';
import AdvertisePopup from './AdvertisePopup';
import { cleanAdCode, initGPT, parseAdCode, executeAdScripts } from '@/lib/gpt-loader';
import { useAdSlot } from './AdDataProvider';
import { useDevice } from '@/contexts/DeviceContext';

// Map position to slot name
const positionToSlotName: Record<string, string> = {
  'big-ad-1': ARTICLE_AD_SLOT_NAMES.BIG_AD_1,
  'small-1': ARTICLE_AD_SLOT_NAMES.SMALL_1,
  'big-ad-2': ARTICLE_AD_SLOT_NAMES.BIG_AD_2,
  'big-ad-3': ARTICLE_AD_SLOT_NAMES.BIG_AD_3,
  'ros-inread': ARTICLE_AD_SLOT_NAMES.ROS_INREAD,
  'first-ad-mobile': ARTICLE_AD_SLOT_NAMES.FIRST_AD_MOBILE,
  'infinite-ad': ARTICLE_AD_SLOT_NAMES.INFINITE_AD
};

// Placeholder sizes to prevent CLS — reserve the LARGEST creative each
// slot can serve so the box never grows after the ad fills in.
//   big-ad-1/2/3 → DIN_Storypage_300x250_300x600 (max 300x600)
//   small-1      → DIN_StoryPage_300x250 (fixed)
//   ros-inread   → in-read 300x250 (1x1 out-of-page, but renders ~300x250)
//   first-ad-mobile → DIN_StoryPage_MOB_300x250_ATF (fixed)
//   infinite-ad  → DIN_Storypage_Infinite_Top_728x90_970x90_970x250 (max 970x250)
const AD_PLACEHOLDER_SIZES: Record<string, {
  minWidth: number;
  minHeight: number;
}> = {
  'big-ad-1': {
    minWidth: 300,
    minHeight: 600
  },
  'small-1': {
    minWidth: 300,
    minHeight: 250
  },
  'big-ad-2': {
    minWidth: 300,
    minHeight: 600
  },
  'big-ad-3': {
    minWidth: 300,
    minHeight: 600
  },
  'ros-inread': {
    minWidth: 300,
    minHeight: 250
  },
  'first-ad-mobile': {
    minWidth: 300,
    minHeight: 250
  },
  'infinite-ad': {
    minWidth: 728,
    minHeight: 250
  }
};
function generateUniqueCode(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 6; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}
interface ArticleMultiAdsProps {
  position: 'big-ad-1' | 'small-1' | 'big-ad-2' | 'big-ad-3' | 'ros-inread' | 'first-ad-mobile' | 'infinite-ad';
  className?: string;
}
export default function ArticleMultiAds({
  position,
  className = ''
}: ArticleMultiAdsProps) {
  const adContentRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [adCode, setAdCode] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const {
    isMobile,
    deviceDetected
  } = useDevice();
  const hasInjectedRef = useRef(false);
  const slotDivIdRef = useRef<string | null>(null);
  const uniqueCode = useMemo(() => generateUniqueCode(), []);
  const placeholderSize = AD_PLACEHOLDER_SIZES[position] || {
    minWidth: 300,
    minHeight: 250
  };

  // Load ad — check if already visible, otherwise use IntersectionObserver
  useEffect(() => {
    if (isVisible || !containerRef.current) return;

    // Check if element is already in viewport (above the fold)
    const rect = containerRef.current.getBoundingClientRect();
    if (rect.top < window.innerHeight + 500) {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      });
    }, {
      rootMargin: '500px',
      threshold: 0
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [isVisible]);

  // Read ad data from context (embedded in HTML, zero HTTP calls)
  const slotName = positionToSlotName[position];
  const adSlotData = useAdSlot(slotName);
  useEffect(() => {
    if (!deviceDetected || !isVisible) return;
    if (adSlotData) {
      const code = isMobile ? adSlotData.mobileAdCode || adSlotData.desktopAdCode : adSlotData.desktopAdCode;
      setAdCode(code || null);
    }
    setIsLoading(false);
  }, [adSlotData, isVisible, isMobile, deviceDetected]);

  // Handle ad code injection with unique div IDs — only after device detected
  useEffect(() => {
    if (!deviceDetected || !isVisible || !adCode || !adContentRef.current || hasInjectedRef.current) {
      return;
    }
    hasInjectedRef.current = true;
    const container = adContentRef.current;
    const cleaned = cleanAdCode(adCode);
    if (!cleaned) return;

    // Extract GPT slot parameters directly from the ad code
    const slotMatch = cleaned.match(/defineSlot\(\s*['"]([^'"]+)['"]\s*,\s*(\[[^\]]+\])\s*,\s*['"]([^'"]+)['"]\s*\)/);
    if (!slotMatch) {
      // Fallback: use old script execution approach
      const {
        html,
        scripts
      } = parseAdCode(cleaned);
      container.innerHTML = `<div style="display:flex;justify-content:center;align-items:center;text-align:center;width:100%;">${html}</div>`;
      if (scripts.length > 0) {
        executeAdScripts(scripts);
      }
      return;
    }
    const [, slotPath, sizesStr, originalDivId] = slotMatch;
    let sizes: any;
    try {
      sizes = JSON.parse(sizesStr);
    } catch {
      sizes = [300, 250];
    }

    // On mobile, ensure mobile-friendly sizes are included so GPT can serve an ad
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      const normalizedSizes: number[][] = Array.isArray(sizes[0]) ? sizes : [sizes];
      const hasSmallSize = normalizedSizes.some((s: number[]) => s[0] <= 336);
      if (!hasSmallSize) {
        sizes = [...normalizedSizes, [300, 250], [336, 280]];
      }
    }

    // Create a unique div ID for this instance
    const uniqueDivId = `${originalDivId}-${uniqueCode}-${position}`;
    slotDivIdRef.current = uniqueDivId;

    // Create the ad container div (no min-width to avoid GPT blocking ads on narrow mobile screens).
    // For multi-size slots, reserve the LARGEST height so the box doesn't grow
    // when a taller creative serves (e.g. 970x250 over 728x90).
    const minH = Array.isArray(sizes[0]) ? Math.max(...(sizes as number[][]).map(s_0 => s_0[1])) : sizes[1] || 250;
    container.innerHTML = `<div id="${uniqueDivId}" style="width:100%;min-height:${minH}px;display:flex;justify-content:center;align-items:center;text-align:center;"></div>`;

    // Use initGPT to ensure GPT is loaded, then define + display + refresh
    // all in a single cmd.push to avoid timing issues
    initGPT(() => {
      const googletag = (window as any).googletag;
      if (!googletag) return;
      googletag.cmd.push(() => {
        try {
          const slot = googletag.defineSlot(slotPath, sizes, uniqueDivId);
          if (slot) {
            slot.addService(googletag.pubads());
            googletag.enableServices();
            googletag.display(uniqueDivId);
            googletag.pubads().refresh([slot]);
          } else {}
        } catch (err) {
          console.error(`[ArticleMultiAds] ✗ ${position}: error`, err);
        }
      });
    });
  }, [adCode, isVisible, position, uniqueCode]);

  // Cleanup: destroy GPT slots on unmount and reset injection flag
  useEffect(() => {
    return () => {
      const divId = slotDivIdRef.current;
      try {
        const googletag_0 = (window as any).googletag;
        if (googletag_0?.pubads && divId) {
          const allSlots = googletag_0.pubads()?.getSlots?.() || [];
          const slotsToDestroy = allSlots.filter((slot_0: any) => slot_0.getSlotElementId() === divId);
          if (slotsToDestroy.length > 0) {
            googletag_0.destroySlots(slotsToDestroy);
          }
        }
      } catch {/* ignore */}
      if (adContentRef.current) {
        adContentRef.current.innerHTML = '';
      }
      // Reset so re-mount can re-inject
      hasInjectedRef.current = false;
      slotDivIdRef.current = null;
    };
  }, [position]);
  return <div ref={containerRef} className={`flex justify-center py-4 ${className}`}>
      <div className="bg-gray-100 p-[5px] relative">
        <div ref={adContentRef} style={{
        minWidth: placeholderSize.minWidth,
        minHeight: placeholderSize.minHeight,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center' as const
      }} />
        <AdvertisePopup />
      </div>
    </div>;
}